import { styled, alpha } from '@mui/material/styles';
import { Button, ListSubheader, Link as MuiLink, Grid } from '@mui/material';
import { isDark } from 'global/styles';
import { SYSTEM_HEADER_ROW_HEIGHT } from '../AssessmentScore/styles';

const IMAGE = 100;

export const CpScoreHeaderChild = styled('h3')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'block',
  },
  margin: 0,
  marginLeft: theme.spacing(1),
  // Checklist header score is an h3 (browser-bold); keep the score at normal weight.
  fontWeight: 'normal',
}));

export const CpScoreHeaderChildTypo = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'block',
  },
  width: 48,
  minWidth: 48,
  margin: 0,
}));

export const LoadContainer = styled('div')({
  height: 4,
  width: '100%',
});

export const HeaderChildName = styled('div')({
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  overflowX: 'auto',
  // PFM-2615: stack the checklist name and its weight/criteria sub-label
  // vertically so the absolutely-removed criteria no longer overlaps the name
  // (MUI v5->v9 layout regression).
  display: 'flex',
  flexDirection: 'column',
  // Top-align the name above the weight so the name (first line) lines up with
  // the checkbox and the weight hangs below it (the header top-aligns its row
  // when weighted — see ChecklistHeader).
  justifyContent: 'flex-start',
  // ChecklistHeader (ListSubheader) carries a 48px line-height; without a normal
  // line-height here the name line alone is 48px and the stacked weight label
  // pushes the row taller than unweighted headers (staggered). Keep it compact so
  // weighted/unweighted checklist headers stay the same height.
  lineHeight: 1.2,
});

export const HeaderChildNameH3 = styled('h3')({
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  overflowX: 'auto',
});

export const CHECKLIST_HEADER_BORDER_CLASS = 'with-border';
export const CHECKLIST_HEADER_STICK_TOP_CLASS = 'stick-top';
export const PRINT_VIEW_CLASS = 'print-view';
export const CHECKPOINT_EDIT_CLASS = 'checkpoint-edit';

export const ChecklistHeader = styled(ListSubheader)(({ theme }) => ({
  // PFM-2601: When the Superset header is hidden (single-superset + single-checklist),
  // the Checklist header becomes the only sticky header and should sit at top: 0
  // instead of below the (absent) Superset header. Include the system header's
  // sticky top padding so the checklist row sits flush under the system text.
  top: `calc(${SYSTEM_HEADER_ROW_HEIGHT}px + ${theme.spacing(1)})`,
  zIndex: 2,
  paddingLeft: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  clear: 'both',
  // PFM-2615: when the checklist header has a weight/criteria sub-label, top-align
  // the row so the checkbox and the name line up at the top with the weight below
  // the name (matching prod). Unweighted headers stay vertically centered.
  '&:has(.weight-criteria)': {
    alignItems: 'flex-start',
  },
  [`&.${CHECKLIST_HEADER_STICK_TOP_CLASS}`]: {
    top: 0,
  },
  [`&.${CHECKLIST_HEADER_BORDER_CLASS}`]: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const ChecklistHeaderCourse = styled(ListSubheader)(({ theme }) => ({
  zIndex: 2,
  paddingLeft: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
}));

export const HeaderBtn = styled(Button)(({ theme }) => ({
  color: theme.palette.error.main,
}));

export const HeaderCriteria = styled('span')(({ theme }) => ({
  // PFM-2615: in normal flow below the checklist name (see HeaderChildName
  // column layout) rather than `position: absolute`, which overlapped the name
  // after the MUI v9 upgrade.
  lineHeight: 1.2,
  fontSize: '10px',
  color: theme.palette.text.secondary,
  fontWeight: 'normal',
}));

export const ChecklistImage = styled('div')(({ theme }) => ({
  cursor: 'pointer',
  '& :hover': {
    opacity: 0.8,
  },

  '& .IMAGE': {
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      top: 110,
    },
  },
}));

export const CSRow = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
});

export const CSRowTop = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
});

export const MoreIcon = styled('div')({
  marginLeft: 'auto',
});

export const ChecklistLink = styled(MuiLink)(({ theme }) => ({
  marginLeft: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    marginLeft: theme.spacing(2),
  },
  color: theme.palette.secondary.main,
  textDecoration: 'none',
  fontStyle: 'italic',
  fontWeight: 'bold',
  fontSize: 14,
  padding: theme.spacing(1),
}));

export const Images = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  [`&.${PRINT_VIEW_CLASS} .caption`]: {
    color: theme.palette.text.primary,
  },
  // Score and instance thumbnails stay square when the file is the original
  // (no thumb conversion yet). Cover clips the longer sides.
  '&& .smallMediaContainer': {
    width: '100%',
    height: 'auto !important',
    aspectRatio: '1',
    overflow: 'hidden',
  },
  '&& .smallMediaContainer > div': {
    width: '100%',
    height: '100%',
  },
  '&& .smallMediaContainer img': {
    objectFit: 'cover',
  },
}));

export const CSTopic = styled('div')({
  padding: '6px 6px 6px 16px',
  pageBreakInside: 'avoid !important',
});

export const CSTopicEdit = styled('div')(({ theme }) => ({
  padding: '6px 6px 6px 16px',
  marginBottom: theme.spacing(2),
}));

export const CSTopicFlex = styled('div')({
  display: 'flex',
});

export const CpScoreHidden = styled('div', {
  shouldForwardProp: (prop) => prop !== 'flush',
})(({ theme, flush }) => ({
  marginLeft: flush ? 0 : 76,
  [theme.breakpoints.down('sm')]: {
    marginLeft: flush ? 0 : 28,
  },
}));

export const Criteria = styled('div')(({ theme }) => ({
  color: isDark(theme) ? theme.palette.text.primary : 'red',
  fontSize: 12,
  marginBottom: theme.spacing(1),
}));

export const Grow = styled('div')({
  flexGrow: 1,
});

export const EditSpan = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(1),
  [theme.breakpoints.down('xl')]: {
    display: 'none',
  },
}));

export const SpacerTop = styled('div')(({ theme }) => ({
  height: theme.spacing(2),
}));

export const SpacerBottom = styled('div')(({ theme }) => ({
  height: theme.spacing(4),
}));

// Grid-wrapped variants where layout requires Grid behavior
export const SpacerTopGrid = styled(Grid)(({ theme }) => ({
  height: theme.spacing(2),
}));
export const SpacerBottomGrid = styled(Grid)(({ theme }) => ({
  height: theme.spacing(4),
}));
export const ChecklistImageGrid = styled(Grid)(({ theme }) => ({
  cursor: 'pointer',
  '& :hover': { opacity: 0.8 },
  '& .IMAGE': {
    [theme.breakpoints.up('md')]: { position: 'sticky', top: 110 },
  },
}));

export const RoomBtnContainer = styled('div')(({ theme }) => ({
  margin: `${theme.spacing(1)} 0px`,

  '& .roomBtn': {
    marginLeft: 0,
  },
}));

export const RComment = styled('div')(({ theme }) => ({
  width: '100%',
  backgroundColor: alpha(theme.palette.error.main, 0.2),
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  boxSizing: 'border-box',
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(4),
  maxWidth: `calc(100% - ${theme.spacing(4)})`,
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    maxWidth: '100%',
  },
  [`&.${CHECKPOINT_EDIT_CLASS}`]: {
    marginLeft: 0,
    maxWidth: '100%',
  },

  '& .column': {
    maxWidth: `calc(100% - ${theme.spacing(5)})`,
    alignItems: 'flex-start',
    marginLeft: theme.spacing(4),
  },

  '& .textContainer': {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: '100%',
  },

  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .textRow': {
    justifyContent: 'space-between',
    position: 'relative',

    '& .indicator': {
      position: 'absolute',
      left: -20,
      width: 16,
      height: 16,
      borderRadius: 16,
      backgroundColor: theme.palette.common.black,
    },
  },
  '& .btnRow': {
    justifyContent: 'flex-end',

    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
}));

export const FixedBottomNav = styled('div')(({ theme }) => ({
  position: 'relative',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',

  '& button': {
    marginRight: theme.spacing(1),
  },
}));

export const ButtonRoot = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    minWidth: 48,
  },
}));
